import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Resolves a user's effective role from user_profiles, with a
 * NEXT_PUBLIC_ADMIN_EMAILS fallback for first-login bootstraps where
 * the profile row may not exist yet.
 *
 * Shared by /app/login/page.tsx (one-shot at sign-in) and the
 * AuthContext (cached + reused throughout the session). Previously
 * two near-identical copies of this logic existed and drifted —
 * which caused the "admin keeps getting bounced to /client-portal"
 * bug fixed earlier this session.
 *
 * Returns one of: 'super_admin' | 'admin' | 'ops_admin' |
 * 'teacher_checkin' | 'teacher' | 'family'.
 */
export async function resolveRoleFromProfile(
  supabase: SupabaseClient,
  userId: string,
  userEmail: string
): Promise<string> {
  try {
    const adminEmails = (process.env.NEXT_PUBLIC_ADMIN_EMAILS || '')
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    const { data } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (data?.role) {
      // These tiers are returned as-is.
      if (['teacher_checkin', 'ops_admin', 'super_admin'].includes(data.role)) {
        return data.role;
      }
      // Legacy 'admin' rows or explicit env-listed admins promote to super_admin.
      if (data.role === 'admin' || adminEmails.includes(userEmail.toLowerCase())) {
        return 'super_admin';
      }
      return data.role;
    }

    // No profile row found — env fallback for first-login admins.
    if (adminEmails.includes(userEmail.toLowerCase())) return 'super_admin';
    return 'family';
  } catch {
    // Treat any error as a non-admin to fail closed.
    return 'family';
  }
}
